import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, {useEffect, useState, useRef} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import { getBatchSelectionConfig, getSelectedBatchIds, handleSelectBatchModal, openSelectBatchModal, setSelectedBatchIds, showAlertSnackbar } from '../../../reducers/uiSlice';
import { assignOrderToDriver, createSubBatch, getBatchInfo, getBatchNumber, getBatchOrderArea, getDriverOrderCount } from '../../../actions/mmActions';
import TransferList from './TransferList';
import SelectBatchTable from './SelectBatchTable';
import { getAccountFilteredDistricts, getRefererFromId, getUserPreference } from '../../../utilities/common';
import * as mmHelper from '../../../network/mmHelper';
import { useAlert, usePrevious } from '../../../utilities/hooks';
import { useTranslation } from 'react-i18next';
import { operationLog } from '../../../actions/commonAction';
import moment from 'moment/moment';
import FullscreenLoading from "../../Common/FullscreenLoading";

const useStyles = makeStyles((theme)=> ({
    dialogPaper: {
        minHeight: '50vh'
    }
}))

function SelectBatchModal(props){
    const alertDialog = useAlert();
    let userPref = getUserPreference();
    const [tableSelection, setTableSelection] = useState([])
    const [currentStep, setCurrentStep] = useState(0)
    const [transferLeft, setTransferLeft] = useState([])
    const [transferRight, setTransferRight] = useState([])
    const [timestamp, setTimestamp] = useState(moment(moment().subtract(30, 'days')).unix());

    const classes = useStyles();
    const {t} = useTranslation('common');
    const dispatch = useDispatch();
    const openModal = useSelector(openSelectBatchModal);
    const mapManagement = useSelector(state => state.mapManagement);
    const selectedBatchIds = useSelector(getSelectedBatchIds);
    const batchSelectionConfig = useSelector(getBatchSelectionConfig);

    let prevStep = usePrevious(currentStep)
    let isFinished = useRef(false);

    const wizardSteps = [
        {
            step: 1,
            title: t('mapManagement:select_batch_title'),
            disableGoBack: true,
            disableGoNext: tableSelection.length <= 0,
            tooltip: t('mapManagement:select_batch_next_tooltip')
        },
        {
            step: 2,
            title: t('mapManagement:assign_batch_title'),
            disableGoBack: false,
            nextTitle: t('next'),
            disableGoNext: transferLeft.length > 0 || mapManagement.batchByDistrictLoading,
            tooltip: t('mapManagement:assign_batch_finish_tooltip')
        }
    ]

    const handleGetBatchList = () => {
        dispatch(getBatchNumber(userPref.userCity, userPref.hideAssociatedOrders, userPref.hideSubReferrer, timestamp)).then(data => {
            let errorBatch = _.filter(data, {'refer_type': -1})
            if(errorBatch.length > 0){
                let errorBatchReferer = _.map(errorBatch, 'referer')
                alertDialog.addAlertDialog(
                    t('dialog:batch_error_title'), 
                    t('dialog:batch_error_message', {referer: errorBatchReferer.toString()})
                );
                alertDialog.showAlert();
            }
        })
    }

    const fetchTimestamp = (timestamp) => {
        setTimestamp(moment(timestamp).unix());
    }

    useEffect(() => {
        handleGetBatchList();
    }, [timestamp])

    const allSteps = [
        <SelectBatchTable
            batchList={mapManagement.batches}
            isLoading={mapManagement.batchesLoading}
            selectedBatches={selectedBatchIds}
            onTableSelection={handleTableSelection}
            createSubDisabled={canCreateSubBatch(tableSelection)}
            createSubBatch={(batch_list)=>handleCreateSubBatch(batch_list)}
            fetchBatchList={handleGetBatchList}
            fetchTimestamp={fetchTimestamp}
        />,
        <TransferList 
            transferList={mapManagement.batchByDistrict}
            loadedOrders={mapManagement.orderList}
            isLoading={mapManagement.batchByDistrictLoading}
            accounts={getAccountFilteredDistricts(getVirtualAccounts(mapManagement.staff))}
            onListChange={onTransferListChange}
        />
    ]

    useEffect(()=> {
        if(openModal){
            window.localStorage.setItem('select_batch_count_filter', 0);
            if(selectedBatchIds.length > 0 && currentStep === 1){
                setCurrentStep(1)
                dispatch(getBatchOrderArea(
                    selectedBatchIds, 
                    userPref.hideAssociatedOrders, 
                    userPref.hideSubReferrer))
            }

            //When launching wizard and batch list is empty
            if(currentStep === 0 && mapManagement.batches.length <= 0){
                handleGetBatchList()
            }

            //When coming back from the second page, clear order information
            if(prevStep === 1 && currentStep === 0 && isFinished.current){
                mmHelper.batchChange(
                    dispatch, 
                    userPref.userCity, 
                    selectedBatchIds, 
                    userPref.hideAssociatedOrders, 
                    userPref.hideSubReferrer
                )
            } else if(prevStep === 1 && currentStep === 0 && !isFinished.current) {
                mmHelper.batchChange(dispatch, userPref.userCity, [])
            }
        }
    }, [currentStep, openModal])

    return (
        <Dialog 
            onClose={handleClose} 
            disableBackdropClick={true}
            open={openModal}
            fullWidth={true}
            maxWidth='md'
        >
            <DialogTitle>
                {wizardSteps[currentStep].title}
            </DialogTitle>
            <DialogContent dividers>
                <div style={{width: '100%', height: '650px'}}>
                    {getStepContent(currentStep)}
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} style={{marginRight: 'auto'}}>
                    {t('cancel')}
                </Button>
                <Button onClick={handlePrevStep} disabled={wizardSteps[currentStep].disableGoBack}>
                    {wizardSteps[currentStep].prevTitle || t('previous')}
                </Button>
                <Tooltip title={wizardSteps[currentStep].tooltip}>
                    <span>
                        <Button color='primary' onClick={handleNextStep} disabled={wizardSteps[currentStep].disableGoNext}>
                            {wizardSteps[currentStep].nextTitle || t('next')}
                        </Button>
                    </span>
                </Tooltip>
            </DialogActions>
            <FullscreenLoading open={mapManagement.batchesLoading}/>
        </Dialog>
    )

    function getStepContent(step){
        return (
            <>
            {allSteps.map((stepComponent, index) => {
                return <div style={{width: '100%', height: '100%'}} key={`steps-${index}`} hidden={index !== step}>{stepComponent}</div>
            })}
            </>
        )
    }

    function getVirtualAccounts(accounts){
        return _.filter(accounts, function(account){return account.workingarea === "A" || account.workingarea === "B"})
    }

    function onTransferListChange(left, right){
        setTransferLeft(left)
        setTransferRight(right)
    }

    function canCreateSubBatch(selected){
        return selected.length <= 0 || _.some(getRefererFromId(mapManagement.batches, selected), 'SUB')
    }

    function handleClose(){
        dispatch(handleSelectBatchModal(false))
    }

    function handleTableSelection(selectedRows){
        setTableSelection(selectedRows)
    }

    function handleCreateSubBatch(selected){
        dispatch(createSubBatch(getRefererFromId(mapManagement.batches, selected))).then(()=>{
            //dispatch(getBatchNumber(userPref.userCity))
            dispatch(showAlertSnackbar({title: "Create Sub-Batch", message: "Might take up to 10 minutes to show the created batch. Requires manual refresh", type: 'success'}))
        }).catch((error)=>{
            dispatch(showAlertSnackbar({title: "ERROR: Create Sub-Batch", message: error.message}))
        })
    }

    function handleNextStep(){
        let nextStep = currentStep + 1
        let ids = getRefererFromId(mapManagement.batches, tableSelection)
        if (nextStep === 1){
            props.handleReset(true);
            if(ids.length >= 10){
                dispatch(operationLog(0, userPref.username, 0, 1, `${userPref.username} Selected ${ids.length} batches`))
            }
            dispatch(getBatchInfo(userPref.userCity, ids, userPref.hideAssociatedOrders, userPref.hideSubReferrer))
            //dispatch(getBatchOrderArea(ids, userPref.hideAssociatedOrders))
            dispatch(getDriverOrderCount(userPref.userCity, ids, userPref.hideAssociatedOrders, userPref.hideSubReferrer))
            dispatch(setSelectedBatchIds(ids))
        } else if (nextStep === 2) {
            if(transferRight.length > 0){
                let promises = []
                let transformedObject = {}

                _.map(transferRight, (i)=>{
                    transformedObject[i.assign_to_account] = _.concat(transformedObject[i.assign_to_account], i.orderIds)
                })

                
                _.map(transformedObject, function(value, key){
                    let paramsAssign = {
                        from_driver: null,
                        operator: userPref.username,
                        to_driver: key,
                        orders: value.filter((id) =>
                            id !== null && id !== undefined    
                        )
                    }
                    promises.push(dispatch(assignOrderToDriver(paramsAssign)))
                })

                Promise.all(promises).then((value)=>{
                    //Reset, return to prev page and close
                    isFinished.current = true
                    handlePrevStep()
                    handleClose()
                }).catch((error)=> {
                    console.log(error)
                    dispatch(showAlertSnackbar({title: "Order Transfer Failed", message: error.message}))
                })
            } else {
                handleClose()
            }
        }
        
        if(wizardSteps.length > nextStep){
            setCurrentStep(() => nextStep)
        }
    }

    function handlePrevStep(){
        let _prevStep = prevStep - 1
        setCurrentStep(() => _prevStep);
    }
}

export default SelectBatchModal